import { useCallback, useMemo, useState } from 'react';
import {
  BaseTable,
  type TableColumnDefinition,
} from '@amzn/gsce-shared-react-lib/components/table';
import { TriggerButton } from '@amzn/stencil-react-components/helpers';
import { Col, Row, View } from '@amzn/stencil-react-components/layout';
import { TableSpacing } from '@amzn/stencil-react-components/table';
import { Text } from '@amzn/stencil-react-components/text';

import { useMessages, type Messages } from 'src/v2/shared/i18n/useMessages';
import {
  buildImpactShareMap,
  flattenVisibleRows,
  riskBar,
  type FlatMetricRow,
  type RiskMetricNode,
} from '../logic/riskMetricGroup';
import {
  RISK_TABLE_SKELETON_ROW_COUNT,
  formatRiskPercent,
  formatRiskValue,
} from '../logic/riskScorePresentation';
import { RISK_ENGINE_BUNDLE, type RiskEngineMessageKey } from '../translations';
import { RISK_ENGINE_TEST_IDS } from '../testIds';
import { ImpactBar } from './ImpactBar';
import { RiskStatusIndicator } from './RiskStatusIndicator';

type RiskEngineMessages = Messages<RiskEngineMessageKey>;

/** A visible tree row keyed for the shared table, which tracks rows by a string property. */
interface TreeTableRow extends FlatMetricRow {
  readonly rowId: string;
}

const RISK_TREE_TABLE_ID = 'risk-engine-breakdown-drivers';

/** Pixels of indent per hierarchy level, and the width reserved for the chevron so leaf rows line up. */
const INDENT_PER_LEVEL_PX = 20;
const CHEVRON_SLOT_PX = 28;

/** Column widths, left to right. The impact bar gets the most room after the metric name. */
const COLUMN_WIDTHS = {
  metric: '30%',
  status: '12%',
  impact: '26%',
  actual: '10%',
  target: '10%',
  percentOfTarget: '12%',
} as const;

/** Number of columns in the driver table; sizes the page skeleton shown before translations load. */
export const RISK_TREE_COLUMN_COUNT = Object.keys(COLUMN_WIDTHS).length;

/**
 * Value columns are center-aligned. The Metric column stays left-aligned so the per-depth indent
 * reads as hierarchy.
 */
const CENTER = 'center' as const;
const LEFT = 'left' as const;

const NO_ROWS: TreeTableRow[] = [];

interface RiskMetricTreeTableProps {
  /** The root KPI's drivers — the top-level rows. Each may have its own descendants. */
  readonly roots: readonly RiskMetricNode[];
  readonly ariaLabel: string;
  /** Shows the shared table skeleton (real headers, placeholder rows) while the group loads. */
  readonly loading?: boolean;
}

interface MetricCellProps {
  readonly row: FlatMetricRow;
  readonly expanded: boolean;
  readonly onToggle: (id: string) => void;
  readonly f: RiskEngineMessages['f'];
}

/** The first column: depth indent, a chevron toggle for rows with children, the name, and the reason. */
function MetricCell({ row, expanded, onToggle, f }: MetricCellProps): JSX.Element {
  const { node, hasChildren } = row;
  const { metric, depth } = node;
  const id = metric.metric;
  const name = metric.displayName ?? metric.metric;
  const indentPx = (depth - 1) * INDENT_PER_LEVEL_PX;
  const handleToggle = useCallback(() => onToggle(id), [onToggle, id]);

  return (
    <Col gridGap="dimensions.spacing.50" minWidth={0}>
      <Row
        alignItems="center"
        gridGap="dimensions.spacing.100"
        padding={{ left: indentPx }}
      >
        {hasChildren ? (
          <TriggerButton
            isExpanded={expanded}
            onClick={handleToggle}
            iconAltText={f('expandRow', { metric: name })}
          />
        ) : (
          <View width={CHEVRON_SLOT_PX} aria-hidden />
        )}
        <Text fontWeight="bold">{name}</Text>
      </Row>
      {metric.statusReason ? (
        <View padding={{ left: indentPx + CHEVRON_SLOT_PX }}>
          <Text fontSize="T100" color="color.text.secondary">
            {metric.statusReason}
          </Text>
        </View>
      ) : null}
    </Col>
  );
}

/**
 * The drill-down as a data-first table on the shared `BaseTable` (the same table the list page
 * uses), fed a flattened, indented set of visible rows. Expansion is ours — a chevron in the metric
 * cell toggles an id in local state, and `flattenVisibleRows` recomputes which descendants show —
 * so the table nests to any depth without relying on a one-level expandable-row feature. Impact
 * share is the API's unsigned contribution to the parent. Starts collapsed: only the top-level
 * drivers show. While `loading`, the shared table shows its skeleton under the real headers.
 */
export function RiskMetricTreeTable({
  roots,
  ariaLabel,
  loading = false,
}: RiskMetricTreeTableProps): JSX.Element {
  const { t, f } = useMessages<RiskEngineMessageKey>(RISK_ENGINE_BUNDLE);
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(() => new Set());

  const onToggle = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const shareMap = useMemo(() => buildImpactShareMap(roots), [roots]);
  const rows = useMemo<TreeTableRow[]>(() => {
    if (loading) return NO_ROWS;
    return flattenVisibleRows(roots, expandedIds).map((row) => ({
      ...row,
      rowId: row.node.metric.metric,
    }));
  }, [loading, roots, expandedIds]);

  // Resolve headers to values and list them in the memo deps so the columns rebuild once the i18n
  // bundle finishes loading. A stable `t` identity across that async load would otherwise leave the
  // columns memoized with the raw message keys showing.
  const headingMetric = t('columnMetric');
  const headingStatus = t('columnRiskStatus');
  const headingImpact = t('columnImpact');
  const headingActual = t('columnActual');
  const headingTarget = t('columnTarget');
  const headingPercentOfTarget = t('nodePercentOfTarget');

  const columns = useMemo<TableColumnDefinition<TreeTableRow>[]>(
    () => [
      {
        id: 'metric',
        header: headingMetric,
        textAlign: LEFT,
        width: COLUMN_WIDTHS.metric,
        cell: (row) => (
          <MetricCell
            row={row}
            expanded={expandedIds.has(row.rowId)}
            onToggle={onToggle}
            f={f}
          />
        ),
      },
      {
        id: 'status',
        header: headingStatus,
        textAlign: CENTER,
        width: COLUMN_WIDTHS.status,
        cell: (row) => (
          <RiskStatusIndicator status={row.node.metric.status} t={t} centered />
        ),
      },
      {
        id: 'impact',
        header: headingImpact,
        textAlign: CENTER,
        width: COLUMN_WIDTHS.impact,
        cell: (row) => {
          const share = shareMap.get(row.rowId);
          return share ? (
            <ImpactBar share={share} label={formatRiskPercent(share.sharePct)} />
          ) : (
            <Text>—</Text>
          );
        },
      },
      {
        id: 'actual',
        header: headingActual,
        textAlign: CENTER,
        width: COLUMN_WIDTHS.actual,
        cell: (row) => (
          <Text>
            {formatRiskValue(row.node.metric.actualValue, row.node.metric.metricUnit)}
          </Text>
        ),
      },
      {
        id: 'target',
        header: headingTarget,
        textAlign: CENTER,
        width: COLUMN_WIDTHS.target,
        cell: (row) => (
          <Text>
            {formatRiskValue(row.node.metric.targetValue, row.node.metric.metricUnit)}
          </Text>
        ),
      },
      {
        id: 'percentOfTarget',
        header: headingPercentOfTarget,
        textAlign: CENTER,
        width: COLUMN_WIDTHS.percentOfTarget,
        cell: (row) => {
          const bar = riskBar(row.node.metric);
          return <Text>{bar ? formatRiskPercent(bar.percentOfTarget) : '—'}</Text>;
        },
      },
    ],
    [
      headingMetric,
      headingStatus,
      headingImpact,
      headingActual,
      headingTarget,
      headingPercentOfTarget,
      t,
      f,
      onToggle,
      expandedIds,
      shareMap,
    ],
  );

  return (
    <View dataTestId={RISK_ENGINE_TEST_IDS.tree} width="100%">
      <BaseTable
        tableId={RISK_TREE_TABLE_ID}
        items={rows}
        columnDefinitions={columns}
        trackBy="rowId"
        aria-label={ariaLabel}
        loading={loading}
        skeletonRowCount={RISK_TABLE_SKELETON_ROW_COUNT}
        spacing={TableSpacing.Reduced}
        shouldScrollHorizontally={false}
        fullWidth
        showSettings={false}
      />
    </View>
  );
}
